package com.havn.app.features.dashboard.presentation

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.designsystem.havnRefreshStartGate
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnLargePropertyPhotoSource
import com.havn.app.ui.theme.LocalHavnThemeTokens
import com.havn.app.features.agency.presentation.AgencyUiState
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.presentation.LeadsViewModel
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseStatus
import com.havn.app.features.openhouses.presentation.OpenHousesViewModel
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.domain.PropertyPhoto
import com.havn.app.features.properties.domain.PropertyStatus
import com.havn.app.features.properties.presentation.PropertiesViewModel
import com.havn.app.features.reports.presentation.SellerReportsViewModel
import com.havn.app.features.agency.domain.Agency
import com.havn.app.features.agency.domain.AgencyInvitation

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DashboardScreen(
    authRepository: AuthRepository,
    agencyViewModel: AgencyViewModel,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    sellerReportsViewModel: SellerReportsViewModel,
    onOpenOpening: (String) -> Unit,
    onViewProperty: (String) -> Unit,
    onOpenLeads: () -> Unit,
    onOpenProperties: () -> Unit,
    onOpenReports: () -> Unit,
    onOpenAnalytics: () -> Unit,
    onOpenTasks: () -> Unit,
    onAddProperty: () -> Unit,
    onScheduleOpenHouse: (String) -> Unit,
    onOpenSettings: () -> Unit = {},
    onOpenAgency: () -> Unit = {},
    showHeaderActions: Boolean = true
) {
    val propertiesState by propertiesViewModel.uiState.collectAsState()
    val openHousesState by openHousesViewModel.uiState.collectAsState()
    val leadsState by leadsViewModel.uiState.collectAsState()
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val authState by authRepository.authState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val userId = (authState as? AuthState.LoggedIn)?.user?.id
    var firstName by remember(userId) { mutableStateOf<String?>(null) }

    LaunchedEffect(userId) {
        firstName = null
        if (userId != null) {
            authRepository.agentProfile(userId)
                .onSuccess { profile -> firstName = profile.firstName.takeIf { it.isNotBlank() } }
        }
    }

    val featuredOpenHouse = openHousesState.openHouses.firstOrNull { it.status == OpenHouseStatus.Live }
        ?: openHousesState.openHouses.firstOrNull { it.status == OpenHouseStatus.Scheduled }
    val featuredProperty = featuredOpenHouse?.let { openHouse ->
        propertiesState.properties.firstOrNull { it.id == openHouse.propertyId }
    } ?: propertiesState.properties.firstOrNull { it.status == PropertyStatus.Active }
        ?: propertiesState.properties.firstOrNull { it.status == PropertyStatus.Planned }
    LaunchedEffect(featuredProperty?.id) {
        featuredProperty?.id?.let(propertiesViewModel::warmPropertyPhotos)
    }
    val pendingFollowUps = followUpsState.followUps.count { it.status == FollowUpStatus.Pending }
    val recentCheckIns = leadsState.leads
        .take(3)
        .map { lead -> "${lead.fullName} · ${lead.sourceProperty.title}" }
    val nextOpeningLine = featuredOpenHouse?.let { opening ->
        "${opening.displayTitle} · ${opening.startsAt.dateLabel()} · ${opening.timeRange()}"
    } ?: strings.noOpenHouseScheduled
    val nextAction = when {
        featuredOpenHouse != null -> strings.openKioskForNextOpening
        featuredProperty != null -> strings.createOpeningToStart
        else -> strings.addFirstPropertyToStart
    }
    val refreshing = propertiesState.isLoading ||
        openHousesState.isLoading ||
        leadsState.isLoading ||
        followUpsState.isLoading ||
        agencyState.isLoading
    val scrollState = rememberScrollState()
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    var showAgencyChooser by remember { mutableStateOf(false) }

    HavnPullToRefreshBox(
        isRefreshing = refreshing,
        onRefresh = {
            if (gestureStartedAtTop) {
                propertiesViewModel.refresh()
                openHousesViewModel.refresh()
                leadsViewModel.refresh()
                followUpsViewModel.refresh()
                agencyViewModel.refresh()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(scrollState) { gestureStartedAtTop = it }
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.background, theme.gradientStart, theme.background)))
                .verticalScroll(scrollState)
                .padding(horizontal = if (havnIsWebShell) 28.dp else 18.dp, vertical = if (havnIsWebShell) 24.dp else 18.dp)
        ) {
            val wide = havnIsWebShell && maxWidth >= 980.dp
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(if (wide) 18.dp else 14.dp)
            ) {
                TodayHeader(
                    firstName = firstName,
                    workspaceName = agencyState.workspaceName,
                    agencyIconUrl = agencyState.selectedAgency?.iconUrl ?: agencyState.agency?.iconUrl,
                    showAgencyButton = showHeaderActions && agencyState.entitlement.isProAgent,
                    showSettingsButton = showHeaderActions,
                    onOpenSettings = onOpenSettings,
                    onOpenAgency = { showAgencyChooser = true }
                )

                if (showHeaderActions && agencyState.entitlement.isProAgent) {
                    ProWorkspaceActions(
                        onOpenReports = onOpenReports,
                        onOpenAnalytics = onOpenAnalytics,
                        onOpenTasks = onOpenTasks
                    )
                }

                AgencyInvitationsSection(
                    invitations = agencyState.pendingInvitations,
                    respondingInvitationId = agencyState.respondingInvitationId,
                    onAccept = agencyViewModel::acceptInvitation,
                    onDecline = agencyViewModel::declineInvitation
                )

                if (wide) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        Column(modifier = Modifier.weight(1.55f), verticalArrangement = Arrangement.spacedBy(18.dp)) {
                            TodayPropertyArea(
                                featuredProperty = featuredProperty,
                                featuredOpenHouse = featuredOpenHouse,
                                openings = openHousesState.openHouses,
                                onAddProperty = onAddProperty,
                                onScheduleOpenHouse = { propertyId -> onScheduleOpenHouse(propertyId) },
                                onStartGuestCheckIn = {
                                    featuredOpenHouse?.let { openHousesViewModel.showLiveDashboard(it.id) }
                                        ?: openHousesState.openHouses.firstOrNull()?.let { onOpenOpening(it.id) }
                                        ?: onOpenProperties()
                                },
                                onSendFollowUp = onOpenLeads,
                                onOpenOpening = onOpenOpening,
                                onViewProperty = onViewProperty
                            )
                            QuickActions(
                                onQuickSignIn = {
                                    featuredOpenHouse?.let { openHousesViewModel.showLiveDashboard(it.id) }
                                },
                                onNewLead = onOpenLeads,
                                onProperties = onOpenProperties
                            )
                        }
                        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(18.dp)) {
                            TodayActionPanel(
                                nextOpening = nextOpeningLine,
                                recentCheckIns = recentCheckIns,
                                pendingFollowUps = pendingFollowUps,
                                nextAction = nextAction
                            )
                            TodayOverview(
                                visitors = openHousesState.visitorCount,
                                hotLeads = leadsState.leads.count { it.temperature == LeadTemperature.Hot },
                                followUps = pendingFollowUps
                            )
                        }
                    }
                } else {
                    TodayPropertyArea(
                        featuredProperty = featuredProperty,
                        featuredOpenHouse = featuredOpenHouse,
                        openings = openHousesState.openHouses,
                        onAddProperty = onAddProperty,
                        onScheduleOpenHouse = { propertyId -> onScheduleOpenHouse(propertyId) },
                        onStartGuestCheckIn = {
                            featuredOpenHouse?.let { openHousesViewModel.showLiveDashboard(it.id) }
                                ?: openHousesState.openHouses.firstOrNull()?.let { onOpenOpening(it.id) }
                                ?: onOpenProperties()
                        },
                        onSendFollowUp = onOpenLeads,
                        onOpenOpening = onOpenOpening,
                        onViewProperty = onViewProperty
                    )

                    TodayActionPanel(
                        nextOpening = nextOpeningLine,
                        recentCheckIns = recentCheckIns,
                        pendingFollowUps = pendingFollowUps,
                        nextAction = nextAction
                    )

                    QuickActions(
                        onQuickSignIn = {
                            featuredOpenHouse?.let { openHousesViewModel.showLiveDashboard(it.id) }
                        },
                        onNewLead = onOpenLeads,
                        onProperties = onOpenProperties
                    )

                    TodayOverview(
                        visitors = openHousesState.visitorCount,
                        hotLeads = leadsState.leads.count { it.temperature == LeadTemperature.Hot },
                        followUps = pendingFollowUps
                    )
                }
            }
        }
    }

    if (showAgencyChooser) {
        AgencyChooserDialog(
            state = agencyState,
            onSelectAgency = agencyViewModel::selectAgency,
            onDismiss = { showAgencyChooser = false },
            onGoToAgency = {
                showAgencyChooser = false
                onOpenAgency()
            }
        )
    }
}

@Composable
private fun AgencyInvitationsSection(
    invitations: List<AgencyInvitation>,
    respondingInvitationId: String?,
    onAccept: (String) -> Unit,
    onDecline: (String) -> Unit
) {
    if (invitations.isEmpty()) return

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        invitations.forEach { invitation ->
            AgencyInvitationCard(
                invitation = invitation,
                isResponding = respondingInvitationId == invitation.id,
                onAccept = { onAccept(invitation.id) },
                onDecline = { onDecline(invitation.id) }
            )
        }
    }
}

@Composable
private fun AgencyInvitationCard(
    invitation: AgencyInvitation,
    isResponding: Boolean,
    onAccept: () -> Unit,
    onDecline: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.55f))) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Surface(
                modifier = Modifier.size(42.dp),
                shape = CircleShape,
                color = theme.primaryAccent.copy(alpha = 0.15f),
                border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.35f))
            ) {
                Box(contentAlignment = Alignment.Center) {
                    AgencyLogoImage(
                        iconUrl = invitation.agencyIconUrl,
                        fallbackIcon = HavnIconKey.Users,
                        modifier = Modifier.size(42.dp)
                    )
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    text = "Agency invitation",
                    color = theme.secondaryAccent,
                    style = MaterialTheme.typography.labelLarge
                )
                Text(
                    text = "Join ${invitation.agencyName}",
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    text = "You were invited as ${invitation.role.displayName.lowercase()}${invitation.invitedByName?.let { " by $it" } ?: ""}.",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    HavnButton(
                        text = "Accept",
                        onClick = onAccept,
                        modifier = Modifier.weight(1f),
                        loading = isResponding,
                        enabled = !isResponding,
                        leadingIcon = HavnIconKey.Check
                    )
                    HavnButton(
                        text = "Decline",
                        onClick = onDecline,
                        modifier = Modifier.weight(1f),
                        style = HavnButtonStyle.Secondary,
                        enabled = !isResponding
                    )
                }
            }
        }
    }
}

@Composable
private fun TodayPropertyArea(
    featuredProperty: Property?,
    featuredOpenHouse: OpenHouse?,
    openings: List<OpenHouse>,
    onAddProperty: () -> Unit,
    onScheduleOpenHouse: (String) -> Unit,
    onStartGuestCheckIn: () -> Unit,
    onSendFollowUp: () -> Unit,
    onOpenOpening: (String) -> Unit,
    onViewProperty: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        if (featuredProperty == null) {
            EmptyState(
                title = strings.noActivePropertiesTitle,
                body = strings.noActivePropertiesBody
            )
            FirstRunGuide(
                onAddProperty = onAddProperty,
                onScheduleOpenHouse = { featuredProperty?.let { onScheduleOpenHouse(it.id) } ?: onAddProperty() },
                onStartGuestCheckIn = onStartGuestCheckIn,
                onSendFollowUp = onSendFollowUp
            )
        } else {
            FeaturedOpenHouseCard(
                property = featuredProperty,
                openHouse = featuredOpenHouse,
                openings = openings,
                onOpenOpening = onOpenOpening,
                onViewProperty = { onViewProperty(featuredProperty.id) }
            )
        }
    }
}

@Composable
private fun ProWorkspaceActions(
    onOpenReports: () -> Unit,
    onOpenAnalytics: () -> Unit,
    onOpenTasks: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        QuickActionTile(
            icon = HavnIconKey.Check,
            title = "Tasks",
            body = "Queue",
            accent = theme.success,
            modifier = Modifier.weight(1f),
            onClick = onOpenTasks
        )
        QuickActionTile(
            icon = HavnIconKey.Reports,
            title = "Reports",
            body = "Seller",
            accent = theme.primaryAccent,
            modifier = Modifier.weight(1f),
            onClick = onOpenReports
        )
        QuickActionTile(
            icon = HavnIconKey.Chart,
            title = "Analytics",
            body = "Trends",
            accent = theme.cold,
            modifier = Modifier.weight(1f),
            onClick = onOpenAnalytics
        )
    }
}

@Composable
private fun AgencyChooserDialog(
    state: AgencyUiState,
    onSelectAgency: (String) -> Unit,
    onDismiss: () -> Unit,
    onGoToAgency: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.surface,
        title = {
            Text(strings.commonAgency, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                state.agencies.forEach { agency ->
                    AgencyChooserRow(
                        agency = agency,
                        selected = agency.id == state.selectedAgencyId,
                        onClick = { onSelectAgency(agency.id) }
                    )
                }
                if (state.agencies.isEmpty()) {
                    AgencyChooserRow(
                        title = AgencyUiState.SOLO_AGENCY_NAME,
                        subtitle = "Your default agency",
                        selected = true,
                        onClick = {}
                    )
                }
                Text(
                    "Choose which agency you want to work with. Agency tools include invites, shared leads, and branding.",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        },
        confirmButton = {
            HavnButton(
                text = strings.commonAgency,
                onClick = onGoToAgency,
                leadingIcon = HavnIconKey.ArrowRight
            )
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel, color = theme.primaryAccent)
            }
        }
    )
}

@Composable
private fun AgencyChooserRow(
    agency: Agency,
    selected: Boolean,
    onClick: () -> Unit
) {
    AgencyChooserRow(
        title = agency.name,
        subtitle = "${agency.role.displayName} - ${agency.memberCount} members",
        iconUrl = agency.iconUrl,
        selected = selected,
        onClick = onClick
    )
}

@Composable
private fun AgencyChooserRow(
    title: String,
    subtitle: String,
    iconUrl: String? = null,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.16f) else theme.card,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            AgencyLogoImage(
                iconUrl = iconUrl,
                fallbackIcon = HavnIconKey.Home,
                contentDescription = null,
                modifier = Modifier.size(28.dp)
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
                Text(title, color = theme.primaryText, style = MaterialTheme.typography.titleSmall)
                Text(subtitle, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall)
            }
            if (selected) {
                HavnIcon(
                    icon = HavnIconKey.Check,
                    contentDescription = null,
                    tint = theme.primaryAccent,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    }
}

@Composable
private fun FirstRunGuide(
    onAddProperty: () -> Unit,
    onScheduleOpenHouse: () -> Unit,
    onStartGuestCheckIn: () -> Unit,
    onSendFollowUp: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Start with four simple steps", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            FirstRunStep("1", "Add property", onClick = onAddProperty)
            FirstRunStep("2", "Schedule open house", onClick = onScheduleOpenHouse)
            FirstRunStep("3", "Start guest check-in", onClick = onStartGuestCheckIn)
            FirstRunStep("4", "Send follow-up", onClick = onSendFollowUp)
        }
    }
}

@Composable
private fun FirstRunStep(number: String, label: String, onClick: () -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Surface(
            modifier = Modifier.size(28.dp),
            shape = CircleShape,
            color = theme.primaryAccent.copy(alpha = 0.18f),
            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.55f))
        ) {
            Box(contentAlignment = Alignment.Center) {
                Text(number, color = theme.secondaryAccent, style = MaterialTheme.typography.labelLarge)
            }
        }
        Text(label, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
        HavnIcon(
            icon = HavnIconKey.ArrowRight,
            contentDescription = null,
            tint = theme.primaryAccent,
            modifier = Modifier.size(16.dp)
        )
    }
}

@Composable
private fun TodayHeader(
    firstName: String?,
    workspaceName: String,
    agencyIconUrl: String?,
    showAgencyButton: Boolean,
    showSettingsButton: Boolean,
    onOpenSettings: () -> Unit,
    onOpenAgency: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val displayedWorkspaceName = when (workspaceName) {
        "Personal Workspace" -> strings.personalAccount
        else -> workspaceName
    }
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (showAgencyButton) {
                    TodayIconButton(
                        icon = HavnIconKey.Home,
                        imageUrl = agencyIconUrl,
                        contentDescription = strings.commonAgency,
                        onClick = onOpenAgency
                    )
                }
                Text(
                    text = firstName?.let { "Hi $it" } ?: "Hi Agent",
                    color = theme.primaryText,
                    style = HavnTopHeaderTextStyle
                )
            }
            if (showSettingsButton) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    TodayIconButton(
                        icon = HavnIconKey.Sliders,
                        contentDescription = strings.commonSettings,
                        onClick = onOpenSettings
                    )
                }
            }
        }
        Text(
            displayedWorkspaceName,
            color = theme.secondaryText,
            style = MaterialTheme.typography.bodyMedium
        )
    }
}

@Composable
private fun TodayIconButton(
    icon: HavnIconKey,
    imageUrl: String? = null,
    contentDescription: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .size(42.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            AgencyLogoImage(
                iconUrl = imageUrl,
                fallbackIcon = icon,
                contentDescription = contentDescription,
                modifier = Modifier.size(42.dp)
            )
        }
    }
}

@Composable
private fun AgencyLogoImage(
    iconUrl: String?,
    fallbackIcon: HavnIconKey,
    modifier: Modifier = Modifier,
    contentDescription: String? = null
) {
    val theme = LocalHavnThemeTokens.current
    val rendered = HavnDatabaseImage(
        dataUrl = iconUrl,
        contentDescription = contentDescription,
        modifier = modifier.clip(MaterialTheme.shapes.large),
        contentScale = ContentScale.Crop
    )
    if (!rendered) {
        HavnIcon(
            icon = fallbackIcon,
            contentDescription = contentDescription,
            tint = theme.primaryAccent,
            modifier = Modifier.size(20.dp)
        )
    }
}

@Composable
private fun FeaturedOpenHouseCard(
    property: Property,
    openHouse: OpenHouse?,
    openings: List<OpenHouse>,
    onOpenOpening: (String) -> Unit,
    onViewProperty: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val defaultPhoto = property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }
        ?: property.photos.firstOrNull { it.isHero }
        ?: property.photos.firstOrNull()
    var selectedPhotoId by remember(property.id) { mutableStateOf(defaultPhoto?.id) }
    val selectedPhoto = property.photos.firstOrNull { it.id == selectedPhotoId } ?: defaultPhoto
    HavnCard(
        border = null,
        imageHeader = {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clickable(onClick = onViewProperty)
            ) {
                DashboardPropertyImage(property = property, selectedPhoto = selectedPhoto)
                HavnStatusBadge(
                    status = openHouse?.status.toHavnStatus(property.status),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(10.dp)
                )
                Column(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .fillMaxWidth()
                        .background(Brush.verticalGradient(listOf(Color.Transparent, theme.background.copy(alpha = 0.86f))))
                        .padding(14.dp)
                ) {
                    Text(
                        property.title,
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleLarge,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        property.displayAddress,
                        color = theme.primaryText.copy(alpha = 0.92f),
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        property.displayCity,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            TodayPhotoThumbnailStrip(
                property = property,
                selectedPhotoId = selectedPhoto?.id,
                onPhotoSelected = { selectedPhotoId = it }
            )

            TodayOpeningsList(
                openings = openings,
                featuredOpenHouse = openHouse,
                onOpenOpening = onOpenOpening
            )
        }
    }
}

@Composable
private fun TodayOpeningsList(
    openings: List<OpenHouse>,
    featuredOpenHouse: OpenHouse?,
    onOpenOpening: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val orderedOpenings = openings
        .sortedWith(compareBy<OpenHouse> {
            when (it.status) {
                OpenHouseStatus.Live -> 0
                OpenHouseStatus.Scheduled -> 1
                OpenHouseStatus.Completed -> 2
                OpenHouseStatus.Cancelled -> 3
            }
        }.thenBy { it.startsAt })

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(strings.nextOpening, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
        if (orderedOpenings.isEmpty()) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = theme.background.copy(alpha = 0.35f),
                shape = MaterialTheme.shapes.medium,
                border = BorderStroke(1.dp, theme.border)
            ) {
                Text(
                    text = strings.noOpenHousesBody,
                    modifier = Modifier.padding(14.dp),
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
            }
        } else {
            orderedOpenings.forEach { opening ->
                TodayOpeningRow(
                    opening = opening,
                    highlighted = opening.id == featuredOpenHouse?.id,
                    onClick = { onOpenOpening(opening.id) }
                )
            }
        }
    }
}

@Composable
private fun TodayActionPanel(
    nextOpening: String,
    recentCheckIns: List<String>,
    pendingFollowUps: Int,
    nextAction: String
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.todayActionsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            TodayPriorityRow(
                icon = HavnIconKey.Calendar,
                label = strings.whatOpeningNext,
                value = nextOpening
            )
            TodayPriorityRow(
                icon = HavnIconKey.Users,
                label = strings.whoCheckedIn,
                value = recentCheckIns.firstOrNull() ?: strings.noCheckInsYet,
                extra = recentCheckIns.drop(1)
            )
            TodayPriorityRow(
                icon = HavnIconKey.Message,
                label = strings.whoNeedsFollowUp,
                value = if (pendingFollowUps == 0) strings.noPendingFollowUps else "$pendingFollowUps ${strings.commonFollowUps.lowercase()}"
            )
            TodayPriorityRow(
                icon = HavnIconKey.Check,
                label = strings.whatShouldIDoNow,
                value = nextAction
            )
        }
    }
}

@Composable
private fun TodayPriorityRow(
    icon: HavnIconKey,
    label: String,
    value: String,
    extra: List<String> = emptyList()
) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.Top
    ) {
        Surface(
            modifier = Modifier.size(32.dp),
            shape = CircleShape,
            color = theme.primaryAccent.copy(alpha = 0.14f),
            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.28f))
        ) {
            Box(contentAlignment = Alignment.Center) {
                HavnIcon(icon, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(17.dp))
            }
        }
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text(label, color = theme.secondaryAccent, style = MaterialTheme.typography.labelLarge)
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            extra.forEach { line ->
                Text(line, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

@Composable
private fun TodayOpeningRow(
    opening: OpenHouse,
    highlighted: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        color = if (highlighted) theme.surface else theme.background.copy(alpha = 0.35f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, if (highlighted) theme.primaryAccent.copy(alpha = 0.72f) else theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(3.dp)
            ) {
                Text(
                    text = opening.displayTitle,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = "${opening.startsAt.dateLabel()} · ${opening.timeRange()}",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                HavnStatusBadge(status = opening.status.toHavnStatus(PropertyStatus.Active))
                Text(
                    text = "${opening.stats.visitorCount} leads",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelSmall
                )
            }
            HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(16.dp))
        }
    }
}

@Composable
private fun TodayPhotoThumbnailStrip(
    property: Property,
    selectedPhotoId: String?,
    onPhotoSelected: (String) -> Unit
) {
    val photos = property.photos
    val theme = LocalHavnThemeTokens.current
    if (photos.size <= 1) return

    LazyRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        items(photos, key = { it.id }) { photo ->
            val selected = photo.id == selectedPhotoId
            Box(
                modifier = Modifier
                    .size(52.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .clickable { onPhotoSelected(photo.id) }
                    .border(
                        width = if (selected) 2.dp else 0.dp,
                        color = if (selected) theme.primaryAccent else Color.Transparent,
                        shape = MaterialTheme.shapes.medium
                    )
                    .background(theme.surface)
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = photo.thumbnailDataUrl,
                    contentDescription = photo.altText ?: property.title,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(
                        icon = HavnIconKey.Properties,
                        contentDescription = null,
                        tint = theme.primaryAccent,
                        modifier = Modifier
                            .align(Alignment.Center)
                            .size(20.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun BoxScope.DashboardPropertyImage(
    property: Property,
    selectedPhoto: PropertyPhoto? = null
) {
    val theme = LocalHavnThemeTokens.current
    val heroPhoto = selectedPhoto
        ?: property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }
        ?: property.photos.firstOrNull { it.isHero }
        ?: property.photos.firstOrNull()
    Crossfade(
        targetState = heroPhoto,
        label = "today-photo-transition",
        modifier = Modifier.fillMaxSize()
    ) { photo ->
        Box(
            modifier = Modifier.fillMaxSize()
        ) {
            val thumbnailRendered = HavnDatabaseImage(
                dataUrl = photo?.thumbnailDataUrl,
                contentDescription = property.title,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
            if (!thumbnailRendered) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(
                            Brush.linearGradient(
                                listOf(theme.surface, theme.secondaryAccent.copy(alpha = 0.72f), theme.primaryAccent.copy(alpha = 0.36f))
                            )
                        )
                )
            }
            val fullSizeSource = havnLargePropertyPhotoSource(photo?.storagePath)
                ?.takeUnless { it == photo?.thumbnailDataUrl }
            if (fullSizeSource != null) {
                HavnDatabaseImage(
                    dataUrl = fullSizeSource,
                    contentDescription = property.title,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
            }
        }
    }
}

@Composable
private fun QuickActions(
    onQuickSignIn: () -> Unit,
    onNewLead: () -> Unit,
    onProperties: () -> Unit
) {
    val strings = LocalHavnStrings.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        QuickActionTile(
            icon = HavnIconKey.Check,
            title = strings.quickSignIn,
            body = strings.scanQrCode,
            modifier = Modifier.weight(1f),
            onClick = onQuickSignIn
        )
        QuickActionTile(
            icon = HavnIconKey.User,
            title = strings.newLead,
            body = strings.addManually,
            modifier = Modifier.weight(1f),
            onClick = onNewLead
        )
        QuickActionTile(
            icon = HavnIconKey.Home,
            title = strings.myProperties,
            body = strings.manageListings,
            modifier = Modifier.weight(1f),
            onClick = onProperties
        )
    }
}

@Composable
private fun QuickActionTile(
    icon: HavnIconKey,
    title: String,
    body: String,
    accent: Color? = null,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val actionAccent = accent ?: theme.secondaryAccent
    Surface(
        modifier = modifier.clickable(onClick = onClick),
        color = accent?.copy(alpha = 0.12f) ?: theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, accent?.copy(alpha = 0.46f) ?: theme.border)
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 14.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            HavnIcon(icon, contentDescription = title, tint = actionAccent, modifier = Modifier.size(23.dp))
            Spacer(Modifier.height(8.dp))
            Text(title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(body, color = accent ?: theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
private fun TodayOverview(
    visitors: Int,
    hotLeads: Int,
    followUps: Int
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(strings.todaysOverview, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                Text("${strings.seeAll} >", color = theme.secondaryAccent, style = MaterialTheme.typography.labelLarge)
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                OverviewMetric(visitors.toString(), strings.commonVisitors, Modifier.weight(1f))
                OverviewMetric(hotLeads.toString(), strings.hotLeads, Modifier.weight(1f))
                OverviewMetric(followUps.toString(), strings.commonFollowUps, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun OverviewMetric(value: String, label: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier,
        color = theme.background.copy(alpha = 0.35f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column(
            modifier = Modifier.padding(vertical = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
            Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

private fun OpenHouseStatus?.toHavnStatus(propertyStatus: PropertyStatus): HavnStatus = when (this) {
    OpenHouseStatus.Live -> HavnStatus.Active
    OpenHouseStatus.Scheduled -> HavnStatus.Planned
    OpenHouseStatus.Completed,
    OpenHouseStatus.Cancelled -> HavnStatus.Archived
    null -> when (propertyStatus) {
        PropertyStatus.Active -> HavnStatus.Active
        PropertyStatus.Planned -> HavnStatus.Planned
        PropertyStatus.Archived -> HavnStatus.Archived
    }
}

private fun OpenHouse.timeRange(): String =
    "${startsAt.timeLabel()} - ${endsAt.timeLabel()}"

private fun String.dateLabel(): String =
    substringBefore("T").takeIf { it.isNotBlank() } ?: "Today"

private fun String.timeLabel(): String {
    val raw = substringAfter("T", this).substringBefore(".").substringBefore("+").substringBefore("Z")
    val parts = raw.split(":")
    val hour24 = parts.getOrNull(0)?.toIntOrNull() ?: return raw
    val minute = parts.getOrNull(1)?.toIntOrNull() ?: 0
    val suffix = if (hour24 >= 12) "PM" else "AM"
    val hour12 = when (val normalized = hour24 % 12) {
        0 -> 12
        else -> normalized
    }
    return "$hour12:${minute.toString().padStart(2, '0')} $suffix"
}
